import { useMemo } from "react";
import type { CSSProperties, ReactNode } from "react";
import { Navigate, useNavigate, useParams } from "react-router-dom";
import { Btn } from "@/components/ui/Btn/Btn";
import { Icon, type IconName } from "@/components/ui/Icon/Icon";
import { PlatformBadge, type PlatformKey } from "@/components/ui/PlatformBadge/PlatformBadge";
import { StatusPill } from "@/components/ui/StatusPill/StatusPill";
import { AppShell } from "@/components/app/AppShell";
import { useAuth } from "@/lib/AuthContext";
import { useApproveDraft, useBrand, useDrafts, useRejectDraft, useWorkspace } from "@/lib/queries";
import { toDraftView, identityFromEmail, type DraftView } from "@/lib/draftView";
import { useIsMobile } from "@/lib/useMediaQuery";
import { can } from "@notamen/core/workspace";

const DAY_MS = 24 * 60 * 60 * 1000;

/**
 * Brand home — the mission-control dashboard a user lands on for a brand.
 * Implements the Claude Design "Home" surface (design bundle home.jsx):
 * morning briefing, KPI strip, the review queue front-and-centre, what's
 * going live today, the brain/trends digests, a 7-day calendar peek, brand
 * health, and onboarding.
 *
 * Every value here is REAL: the queue / going-live / calendar / KPIs are
 * derived from the brand's actual drafts, and Approve/Reject act on them.
 * Sections whose backend isn't built yet (overnight brain edits, post
 * performance metrics) show an honest empty state + a deep link rather than
 * fabricated numbers — per the no-placeholder rule in CLAUDE.md.
 */
export function BrandDetail() {
  const { ws, brand } = useParams<{ ws: string; brand: string }>();
  const auth = useAuth();
  const navigate = useNavigate();
  const wsQuery = useWorkspace(ws);
  const brandQuery = useBrand(ws, brand);
  const draftsQuery = useDrafts(ws, brand);
  const approve = useApproveDraft(ws, brand);
  const reject = useRejectDraft(ws, brand);
  const isMobile = useIsMobile();

  const views = useMemo<DraftView[]>(
    () => (draftsQuery.data?.drafts ?? []).map(toDraftView),
    [draftsQuery.data],
  );

  const stats = useMemo(() => deriveStats(views), [views]);

  if (auth.status === "anonymous") {
    return <Navigate to={`/signin?next=/w/${ws}/b/${brand}`} replace />;
  }

  const b = brandQuery.data?.brand;
  const role = wsQuery.data?.role;
  const canApprove = role ? can(role, "approve") : false;
  const canGenerate = role ? can(role, "generate") : false;
  const bp = `/w/${ws}/b/${brand}`;

  const greetingName = capitalize(identityFromEmail(auth.user?.email ?? undefined).name);

  return (
    <AppShell currentWorkspaceId={ws}>
      <div style={{ maxWidth: 1320, margin: "0 auto", padding: isMobile ? "16px 14px 48px" : "24px 28px 60px", display: "flex", flexDirection: "column", gap: isMobile ? 16 : 24 }}>
          {/* Breadcrumb + greeting + header actions */}
          <header style={{ display: "flex", alignItems: "flex-end", justifyContent: "space-between", gap: 16, flexWrap: "wrap" }}>
            <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
              <Row gap={6} align="center">
                <span style={{ fontSize: 12, color: "var(--text-3)" }}>{wsQuery.data?.workspace.name ?? "Workspace"}</span>
                <Icon name="arrow-right" size={11} color="var(--text-3)" />
                <span style={{ fontSize: 12, color: "var(--text-2)", fontWeight: 600 }}>{b?.name ?? "Brand"}</span>
              </Row>
              <h1 style={{ margin: 0, fontSize: 26, fontWeight: 700, letterSpacing: "-0.02em", color: "var(--text-1)" }}>
                {greetingForHour()}{greetingName ? `, ${greetingName}` : ""}
              </h1>
            </div>
            <Row gap={8} style={{ flexWrap: "wrap" }}>
              <Btn href={`${bp}/queue`} variant="secondary" iconLeft={<Icon name="check-circle" size={14} />}>
                Approval queue
              </Btn>
              {canGenerate && (
                <Btn href={`${bp}/generate`} variant="primary" iconLeft={<Icon name="zap" size={14} />}>
                  Generate today's drafts
                </Btn>
              )}
            </Row>
          </header>

          {brandQuery.isLoading && <p style={{ color: "var(--text-3)" }}>Loading brand…</p>}
          {brandQuery.error && (
            <p style={{ color: "var(--danger)" }}>Couldn’t load this brand: {brandQuery.error.message}</p>
          )}

          {b && (
            <>
              <MorningBriefing
                name={greetingName}
                brandName={b.name}
                elevator={b.elevator}
                stats={stats}
                isMobile={isMobile}
                onReview={() => navigate(`${bp}/queue`)}
                onGenerate={canGenerate ? () => navigate(`${bp}/generate`) : undefined}
              />

              <Grid cols={isMobile ? 2 : 3} gap={14}>
                <KpiCard label="Pending review" value={String(stats.pending)} sub={stats.pendingOver24h > 0 ? `${stats.pendingOver24h} over 24h` : "all fresh"} tone="accent" icon="bell" />
                <KpiCard label="Going live today" value={String(stats.goingLiveToday.length)} sub={stats.nextToday ? `next at ${fmtTime(stats.nextToday)}` : "none scheduled"} tone="info" icon="calendar" />
                <KpiCard label="Scheduled · 7d" value={String(stats.scheduledThisWeek)} sub="across your calendar" tone="success" icon="arrow-up-right" />
                <KpiCard label="Approved · waiting" value={String(stats.approved)} sub="ready to schedule" tone="accent" icon="check-circle" />
                <KpiCard label="Publish success · 7d" value={stats.publishRate === null ? "—" : `${stats.publishRate.toFixed(1)}%`} sub={stats.publishRate === null ? "no posts published yet" : `${stats.published} published · ${stats.failed} failed`} tone="warning" icon="check-circle" />
                <KpiCard label="Drafts created" value={String(views.length)} sub="all-time for this brand" mono icon="info" />
              </Grid>

              <Grid cols={isMobile ? 1 : 2} gap={20} alignStart>
                <Col gap={14}>
                  <PendingQueueCard
                    pending={stats.pendingList}
                    total={stats.pending}
                    canApprove={canApprove}
                    onOpenQueue={() => navigate(`${bp}/queue`)}
                    onApprove={(v) => {
                      if (v.hasBlock) navigate(`${bp}/queue`);
                      else approve.mutate({ draftId: v.id });
                    }}
                    onReject={(v) => reject.mutate({ draftId: v.id })}
                    busy={approve.isPending || reject.isPending}
                  />
                  <GoingLiveCard items={stats.goingLiveToday} onOpenCalendar={() => navigate(`${bp}/calendar`)} />
                </Col>
                <Col gap={14}>
                  <BrainLearnedCard onOpen={() => navigate(`${bp}/learning`)} />
                  <TrendsTodayCard onOpen={() => navigate(`${bp}/trends`)} />
                  <CalendarPeek days={stats.next7} onOpenCalendar={() => navigate(`${bp}/calendar`)} />
                </Col>
              </Grid>

              <BrandHealthCard onManage={() => navigate(`${bp}/accounts`)} />

              <Onboarding
                brandName={b.name}
                hasDrafts={views.length > 0}
                onConnect={() => navigate(`${bp}/accounts`)}
                onGenerate={() => navigate(`${bp}/generate`)}
                onTrends={() => navigate(`${bp}/trends`)}
              />
            </>
          )}
      </div>
    </AppShell>
  );
}

// ─── Derived stats from the brand's real drafts ───────────────────────

interface HomeStats {
  pending: number;
  pendingOver24h: number;
  pendingList: DraftView[];
  approved: number;
  published: number;
  failed: number;
  publishRate: number | null;
  goingLiveToday: DraftView[];
  nextToday: string | undefined;
  scheduledThisWeek: number;
  next7: Array<{ label: string; day: number; count: number; today: boolean }>;
}

function deriveStats(views: DraftView[]): HomeStats {
  const now = Date.now();
  const startOfToday = new Date();
  startOfToday.setHours(0, 0, 0, 0);
  const todayStart = startOfToday.getTime();
  const todayEnd = todayStart + DAY_MS;

  const pendingList = views.filter((v) => v.status === "pending");
  const scheduled = views.filter((v) => v.status === "scheduled" && v.scheduledFor);

  const goingLiveToday = scheduled
    .filter((v) => {
      const t = Date.parse(v.scheduledFor!);
      return t >= todayStart && t < todayEnd;
    })
    .sort((a, b) => Date.parse(a.scheduledFor!) - Date.parse(b.scheduledFor!));

  const next7 = Array.from({ length: 7 }, (_, i) => {
    const d = new Date(todayStart + i * DAY_MS);
    const dayStart = d.getTime();
    const count = scheduled.filter((v) => {
      const t = Date.parse(v.scheduledFor!);
      return t >= dayStart && t < dayStart + DAY_MS;
    }).length;
    return {
      label: d.toLocaleDateString(undefined, { weekday: "short", day: "numeric" }),
      day: d.getDate(),
      count,
      today: i === 0,
    };
  });

  const published = views.filter((v) => v.status === "published").length;
  const failed = views.filter((v) => v.status === "failed").length;

  return {
    pending: pendingList.length,
    pendingOver24h: pendingList.filter((v) => v.createdAt && now - Date.parse(v.createdAt) > DAY_MS).length,
    pendingList: pendingList.slice(0, 3),
    approved: views.filter((v) => v.status === "approved").length,
    published,
    failed,
    publishRate: published + failed > 0 ? (published / (published + failed)) * 100 : null,
    goingLiveToday,
    nextToday: goingLiveToday[0]?.scheduledFor,
    scheduledThisWeek: next7.reduce((sum, d) => sum + d.count, 0),
    next7,
  };
}

// ─── Morning briefing ─────────────────────────────────────────────────

function MorningBriefing({
  brandName,
  elevator,
  stats,
  isMobile,
  onReview,
  onGenerate,
}: {
  name: string;
  brandName: string;
  elevator?: string | undefined;
  stats: HomeStats;
  isMobile: boolean;
  onReview: () => void;
  onGenerate?: (() => void) | undefined;
}) {
  const allCaught = stats.pending === 0 && stats.goingLiveToday.length === 0;
  return (
    <section
      style={{
        padding: isMobile ? "18px 16px" : "22px 24px",
        background: "linear-gradient(140deg, var(--accent-subtle), color-mix(in oklab, var(--warning) 8%, transparent))",
        border: "1px solid var(--accent-border)",
        borderRadius: 16,
        display: "grid",
        gridTemplateColumns: isMobile ? "1fr" : "1.5fr 1fr",
        gap: isMobile ? 18 : 32,
        alignItems: "center",
      }}
    >
      <Col gap={10}>
        <Row gap={8} align="center">
          <Chip tone="accent" icon="zap">{todayLabel()} · briefing</Chip>
          <span style={{ fontSize: 11, color: "var(--text-3)", fontFamily: "var(--font-mono)" }}>{brandName}</span>
        </Row>
        <h2 style={{ margin: 0, fontSize: 21, fontWeight: 700, letterSpacing: "-0.018em", lineHeight: 1.4, color: "var(--text-1)" }}>
          {allCaught ? (
            <>
              <span style={{ color: "var(--text-2)" }}>You’re all caught up.</span>{" "}
              Nothing’s waiting in the queue and nothing’s scheduled for today — a good moment to{" "}
              <b style={{ color: "var(--accent)" }}>generate a fresh batch</b> and keep the calendar full.
            </>
          ) : (
            <>
              <span style={{ color: "var(--text-2)" }}>Here’s where things stand.</span>{" "}
              {stats.pending > 0 && (
                <>
                  <b style={{ color: "var(--accent)" }}>{stats.pending} draft{stats.pending === 1 ? "" : "s"}</b> waiting for your review
                </>
              )}
              {stats.pending > 0 && stats.goingLiveToday.length > 0 && ", and "}
              {stats.goingLiveToday.length > 0 && (
                <>
                  <b style={{ color: "var(--info)" }}>{stats.goingLiveToday.length} going live today</b>
                  {stats.nextToday ? <> (next at {fmtTime(stats.nextToday)})</> : null}
                </>
              )}
              .
            </>
          )}
        </h2>
        {elevator && (
          <span style={{ fontSize: 12, color: "var(--text-3)", lineHeight: 1.45 }}>{elevator}</span>
        )}
        <Row gap={6} style={{ marginTop: 4 }}>
          {stats.pending > 0 && (
            <Btn variant="primary" size="sm" iconLeft={<Icon name="bell" size={13} />} iconRight={<Icon name="arrow-right" size={13} />} onClick={onReview}>
              Review {stats.pending} draft{stats.pending === 1 ? "" : "s"}
            </Btn>
          )}
          {onGenerate && (
            <Btn variant={stats.pending > 0 ? "secondary" : "primary"} size="sm" iconLeft={<Icon name="zap" size={13} />} onClick={onGenerate}>
              Generate today's drafts
            </Btn>
          )}
        </Row>
      </Col>
      <Col gap={8} style={{ alignItems: isMobile ? "flex-start" : "flex-end" }}>
        <Row gap={24}>
          <BriefStat label="pending" value={String(stats.pending)} tone={stats.pending > 0 ? "accent" : "default"} />
          <BriefStat label="live today" value={String(stats.goingLiveToday.length)} tone="info" />
          <BriefStat label="scheduled · 7d" value={String(stats.scheduledThisWeek)} tone="default" />
        </Row>
      </Col>
    </section>
  );
}

function BriefStat({ label, value, tone }: { label: string; value: string; tone: "accent" | "info" | "default" }) {
  const c = tone === "accent" ? "var(--accent)" : tone === "info" ? "var(--info)" : "var(--text-1)";
  return (
    <Col gap={2} style={{ alignItems: "flex-end" }}>
      <span style={{ fontSize: 22, fontWeight: 700, color: c, fontFamily: "var(--font-mono)", letterSpacing: "-0.014em", fontVariantNumeric: "tabular-nums" }}>{value}</span>
      <span style={{ fontSize: 10, color: "var(--text-3)", textTransform: "uppercase", letterSpacing: "0.05em", fontWeight: 600 }}>{label}</span>
    </Col>
  );
}

// ─── KPI cards ────────────────────────────────────────────────────────

function KpiCard({ label, value, sub, tone = "default", mono, icon }: { label: string; value: string; sub: string; tone?: "accent" | "info" | "success" | "warning" | "default"; mono?: boolean; icon: IconName }) {
  const colors: Record<string, string> = {
    accent: "var(--accent)",
    info: "var(--info)",
    success: "var(--success)",
    warning: "var(--warning)",
    default: "var(--text-1)",
  };
  const c = colors[tone] ?? "var(--text-1)";
  return (
    <div style={{ padding: 16, background: "var(--bg-elevated)", border: "1px solid var(--border)", borderRadius: 12, display: "flex", flexDirection: "column", gap: 4 }}>
      <Row gap={6} align="center" style={{ marginBottom: 2 }}>
        <Icon name={icon} size={13} color={c} />
        <span style={{ fontSize: 11, fontWeight: 600, color: "var(--text-3)", textTransform: "uppercase", letterSpacing: "0.04em" }}>{label}</span>
      </Row>
      <span style={{ fontSize: 26, fontWeight: 700, color: c, letterSpacing: "-0.022em", fontVariantNumeric: "tabular-nums", fontFamily: mono ? "var(--font-mono)" : "var(--font-sans)" }}>{value}</span>
      <span style={{ fontSize: 11, color: "var(--text-3)" }}>{sub}</span>
    </div>
  );
}

// ─── Pending queue card ──────────────────────────────────────────────

function PendingQueueCard({ pending, total, canApprove, onOpenQueue, onApprove, onReject, busy }: {
  pending: DraftView[];
  total: number;
  canApprove: boolean;
  onOpenQueue: () => void;
  onApprove: (v: DraftView) => void;
  onReject: (v: DraftView) => void;
  busy: boolean;
}) {
  return (
    <Card>
      <CardHeader icon="bell" iconColor="var(--accent)" title="Pending your review" badge={total > 0 ? String(total) : undefined} badgeTone="accent" action={{ label: "Open queue", onClick: onOpenQueue }} />
      {pending.length === 0 ? (
        <EmptyRow text="Nothing waiting — your queue is clear." />
      ) : (
        <Col gap={0}>
          {pending.map((d, i) => (
            <Row key={d.id} gap={12} align="center" style={{ padding: "12px 16px", borderBottom: i === pending.length - 1 ? "none" : "1px solid var(--border)" }}>
              <PlatformBadge platform={d.platform} size={16} />
              <Col gap={2} style={{ flex: 1, minWidth: 0 }}>
                <span style={{ fontSize: 13, fontWeight: 600, color: "var(--text-1)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                  {d.hook || d.body || "Untitled draft"}
                </span>
                <Row gap={6} align="center">
                  <span style={{ fontSize: 11, color: "var(--text-3)" }}>{d.kindLabel}</span>
                  {d.warnings.length > 0 && (
                    <>
                      <span style={{ fontSize: 10.5, color: "var(--text-3)" }}>·</span>
                      <Row gap={3} align="center">
                        <Icon name="alert" size={11} color={d.hasBlock ? "var(--danger)" : "var(--warning)"} />
                        <span style={{ fontSize: 11, color: d.hasBlock ? "var(--danger)" : "var(--warning)", fontWeight: 500 }}>
                          {d.warnings.length} {d.warnings.length === 1 ? "note" : "notes"}
                        </span>
                      </Row>
                    </>
                  )}
                </Row>
              </Col>
              {canApprove ? (
                <Row gap={4}>
                  <Btn variant="ghost" size="sm" iconLeft={<Icon name="x" size={13} />} onClick={() => onReject(d)} disabled={busy}>Reject</Btn>
                  <Btn variant="primary" size="sm" iconLeft={<Icon name="check" size={13} />} onClick={() => onApprove(d)} disabled={busy}>
                    {d.hasBlock ? "Review" : "Approve"}
                  </Btn>
                </Row>
              ) : (
                <Btn variant="ghost" size="sm" iconRight={<Icon name="arrow-right" size={13} />} onClick={onOpenQueue}>View</Btn>
              )}
            </Row>
          ))}
        </Col>
      )}
    </Card>
  );
}

// ─── Going live today card ───────────────────────────────────────────

function GoingLiveCard({ items, onOpenCalendar }: { items: DraftView[]; onOpenCalendar: () => void }) {
  return (
    <Card>
      <CardHeader icon="calendar" iconColor="var(--info)" title="Going live today" badge={items.length > 0 ? String(items.length) : undefined} action={{ label: "Calendar", onClick: onOpenCalendar }} />
      {items.length === 0 ? (
        <EmptyRow text="Nothing scheduled for today." />
      ) : (
        <Col gap={0}>
          {items.map((it, i) => (
            <Row key={it.id} gap={12} align="center" style={{ padding: "12px 16px", borderBottom: i === items.length - 1 ? "none" : "1px solid var(--border)" }}>
              <div style={{ textAlign: "center", minWidth: 52 }}>
                <div style={{ fontFamily: "var(--font-mono)", fontSize: 14, fontWeight: 700, color: "var(--text-1)", lineHeight: 1 }}>{fmtTime(it.scheduledFor)}</div>
                <div style={{ fontSize: 9.5, color: "var(--text-3)", textTransform: "uppercase", letterSpacing: "0.04em", fontWeight: 600, marginTop: 2 }}>{relUntil(it.scheduledFor)}</div>
              </div>
              <div style={{ width: 1, height: 28, background: "var(--border)" }} />
              <PlatformBadge platform={it.platform} size={16} />
              <span style={{ flex: 1, fontSize: 13, color: "var(--text-1)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{it.hook || it.body || "Scheduled post"}</span>
              <StatusPill state="scheduled" size="sm" />
            </Row>
          ))}
        </Col>
      )}
    </Card>
  );
}

// ─── Brain learned card (awaiting learning backend) ──────────────────

function BrainLearnedCard({ onOpen }: { onOpen: () => void }) {
  return (
    <section style={{ background: "var(--bg-elevated)", border: "1px solid var(--accent-border)", borderLeft: "3px solid var(--accent)", borderRadius: 14, padding: 16, display: "flex", flexDirection: "column", gap: 12 }}>
      <CardHeaderInline icon="zap" iconColor="var(--accent)" title="Brain learned overnight" action={{ label: "Learning", onClick: onOpen }} />
      <div style={{ padding: "14px 12px", background: "var(--bg-subtle)", borderRadius: 8, fontSize: 12.5, color: "var(--text-3)", lineHeight: 1.5 }}>
        No learned patterns yet. Once your posts have performance data, the brand brain proposes edits here for you to accept or snooze.
      </div>
    </section>
  );
}

// ─── Trends today card (links into the Trends scanner) ───────────────

function TrendsTodayCard({ onOpen }: { onOpen: () => void }) {
  return (
    <Card pad>
      <CardHeaderInline icon="arrow-up-right" iconColor="var(--success)" title="Trending in your niche" action={{ label: "All trends", onClick: onOpen }} />
      <div style={{ padding: "14px 12px", background: "var(--bg-subtle)", borderRadius: 8, fontSize: 12.5, color: "var(--text-3)", lineHeight: 1.5, marginTop: 12 }}>
        Connect a trend source (Reddit, YouTube, Google Trends, or a custom feed) in the Trends scanner to surface what’s trending in your niche.
      </div>
    </Card>
  );
}

// ─── Calendar peek (next 7 days, real scheduled counts) ──────────────

function CalendarPeek({ days, onOpenCalendar }: { days: HomeStats["next7"]; onOpenCalendar: () => void }) {
  const max = Math.max(1, ...days.map((d) => d.count));
  const total = days.reduce((s, d) => s + d.count, 0);
  return (
    <Card pad>
      <Row gap={8} align="center">
        <Icon name="calendar" size={14} color="var(--text-2)" />
        <span style={{ fontSize: 13, fontWeight: 700, color: "var(--text-1)" }}>Next 7 days</span>
        <span style={{ fontSize: 11, color: "var(--text-3)", fontFamily: "var(--font-mono)" }}>{total} scheduled</span>
        <div style={{ flex: 1 }} />
        <Btn variant="ghost" size="sm" iconRight={<Icon name="arrow-right" size={13} />} onClick={onOpenCalendar}>Calendar</Btn>
      </Row>
      <Row gap={6} align="flex-end" style={{ height: 90, marginTop: 12 }}>
        {days.map((d, i) => (
          <Col key={i} gap={4} style={{ flex: 1, alignItems: "center", height: "100%" }}>
            <div style={{ flex: 1, display: "flex", alignItems: "flex-end", width: "100%" }}>
              <div
                style={{
                  width: "100%",
                  height: `${(d.count / max) * 100}%`,
                  minHeight: d.count > 0 ? 6 : 2,
                  background: d.today ? "var(--accent)" : d.count === 0 ? "var(--bg-inset)" : "color-mix(in oklab, var(--accent) 36%, transparent)",
                  borderRadius: "4px 4px 0 0",
                }}
              />
            </div>
            <span style={{ fontSize: 10, color: d.today ? "var(--accent)" : "var(--text-3)", fontFamily: "var(--font-mono)", fontWeight: d.today ? 700 : 500 }}>{d.label}</span>
            <span style={{ fontSize: 10, color: "var(--text-3)" }}>{d.count}</span>
          </Col>
        ))}
      </Row>
    </Card>
  );
}

// ─── Brand health (social-account connections) ───────────────────────

function BrandHealthCard({ onManage }: { onManage: () => void }) {
  const isMobile = useIsMobile();
  const slots: Array<{ platform: PlatformKey | "slack"; label: string }> = [
    { platform: "ig", label: "Instagram" },
    { platform: "fb", label: "Facebook" },
    { platform: "tt", label: "TikTok" },
    { platform: "slack", label: "Slack approvals" },
  ];
  return (
    <Card pad>
      <Row gap={10} align="center" style={{ marginBottom: 14 }}>
        <Icon name="check-circle" size={14} color="var(--text-2)" />
        <span style={{ fontSize: 13, fontWeight: 700, color: "var(--text-1)" }}>Brand health</span>
        <div style={{ flex: 1 }} />
        <Btn variant="ghost" size="sm" iconRight={<Icon name="arrow-right" size={13} />} onClick={onManage}>Manage accounts</Btn>
      </Row>
      <Grid cols={isMobile ? 2 : 4} gap={10}>
        {slots.map((s) => (
          <div key={s.label} style={{ padding: 14, background: "var(--bg-subtle)", border: "1px solid var(--border)", borderRadius: 10, display: "flex", flexDirection: "column", gap: 8 }}>
            <Row gap={8} align="center">
              {s.platform === "slack" ? <Icon name="slack" size={18} color="oklch(0.50 0.16 290)" /> : <PlatformBadge platform={s.platform} size={18} />}
              <Col gap={1} style={{ flex: 1, minWidth: 0 }}>
                <span style={{ fontSize: 12.5, fontWeight: 600, color: "var(--text-1)" }}>{s.label}</span>
                <span style={{ fontSize: 10.5, color: "var(--text-3)" }}>Not connected</span>
              </Col>
            </Row>
            <Btn variant="secondary" size="sm" iconLeft={<Icon name="link" size={12} />} full onClick={onManage}>Connect</Btn>
          </div>
        ))}
      </Grid>
    </Card>
  );
}

// ─── Onboarding checklist (derived from real state) ──────────────────

function Onboarding({ brandName, hasDrafts, onConnect, onGenerate, onTrends }: {
  brandName: string;
  hasDrafts: boolean;
  onConnect: () => void;
  onGenerate: () => void;
  onTrends: () => void;
}) {
  const isMobile = useIsMobile();
  const items: Array<{ done: boolean; title: string; cta?: { label: string; onClick: () => void } }> = [
    { done: true, title: "Workspace created" },
    { done: true, title: `Brand brain · ${brandName}` },
    { done: hasDrafts, title: "Generate your first drafts", cta: { label: "Generate", onClick: onGenerate } },
    { done: false, title: "Connect a social account", cta: { label: "Connect", onClick: onConnect } },
    { done: false, title: "Set up trend sources", cta: { label: "Set up", onClick: onTrends } },
  ];
  const complete = items.filter((i) => i.done).length;
  return (
    <Card pad>
      <Row gap={10} align="center" style={{ marginBottom: 14 }}>
        <span style={{ fontSize: 13, fontWeight: 700, color: "var(--text-1)" }}>Finish setting up</span>
        <span style={{ fontSize: 11, color: "var(--text-3)", fontFamily: "var(--font-mono)" }}>{complete} / {items.length} complete</span>
        <div style={{ flex: 1, height: 6, background: "var(--bg-inset)", borderRadius: 999, overflow: "hidden", maxWidth: 240 }}>
          <div style={{ width: `${(complete / items.length) * 100}%`, height: "100%", background: "var(--accent)" }} />
        </div>
        <div style={{ flex: 1 }} />
      </Row>
      <Grid cols={isMobile ? 1 : 3} gap={10}>
        {items.map((it, i) => (
          <Row key={i} gap={10} align="center" style={{ padding: 12, background: it.done ? "var(--success-subtle)" : "var(--bg-subtle)", border: `1px solid ${it.done ? "var(--success-border)" : "var(--border)"}`, borderRadius: 10, opacity: it.done ? 0.75 : 1 }}>
            <span style={{ width: 18, height: 18, borderRadius: 999, background: it.done ? "var(--success)" : "var(--bg-elevated)", color: it.done ? "white" : "var(--text-3)", display: "inline-flex", alignItems: "center", justifyContent: "center", border: it.done ? "none" : "1.5px solid var(--border-strong)", flex: "0 0 auto" }}>
              {it.done && <Icon name="check" size={11} strokeWidth={3} />}
            </span>
            <span style={{ flex: 1, fontSize: 12.5, color: "var(--text-1)", lineHeight: 1.4, textDecoration: it.done ? "line-through" : "none" }}>{it.title}</span>
            {!it.done && it.cta && <Btn variant="ghost" size="sm" onClick={it.cta.onClick}>{it.cta.label}</Btn>}
          </Row>
        ))}
      </Grid>
    </Card>
  );
}

// ─── Layout + presentational primitives ──────────────────────────────

function Row({ children, gap = 0, align, style }: { children: ReactNode; gap?: number; align?: CSSProperties["alignItems"]; style?: CSSProperties }) {
  return <div style={{ display: "flex", flexDirection: "row", gap, alignItems: align, ...style }}>{children}</div>;
}

function Col({ children, gap = 0, style }: { children: ReactNode; gap?: number; style?: CSSProperties }) {
  return <div style={{ display: "flex", flexDirection: "column", gap, ...style }}>{children}</div>;
}

function Grid({ children, cols, gap = 0, alignStart }: { children: ReactNode; cols: number; gap?: number; alignStart?: boolean }) {
  return <div style={{ display: "grid", gridTemplateColumns: `repeat(${cols}, minmax(0, 1fr))`, gap, alignItems: alignStart ? "start" : "stretch" }}>{children}</div>;
}

function Chip({ children, tone = "neutral", icon }: { children: ReactNode; tone?: "accent" | "neutral" | "success"; icon?: IconName }) {
  const map: Record<string, { c: string; bg: string; bd: string }> = {
    accent: { c: "var(--accent)", bg: "var(--accent-subtle)", bd: "var(--accent-border)" },
    success: { c: "var(--success)", bg: "var(--success-subtle)", bd: "var(--success-border)" },
    neutral: { c: "var(--text-2)", bg: "var(--bg-subtle)", bd: "var(--border)" },
  };
  const t = map[tone]!;
  return (
    <span style={{ display: "inline-flex", alignItems: "center", gap: 4, padding: "2px 8px", borderRadius: 999, fontSize: 11, fontWeight: 600, color: t.c, background: t.bg, border: `1px solid ${t.bd}` }}>
      {icon && <Icon name={icon} size={11} color={t.c} />}
      {children}
    </span>
  );
}

function Card({ children, pad }: { children: ReactNode; pad?: boolean }) {
  return <section style={{ background: "var(--bg-elevated)", border: "1px solid var(--border)", borderRadius: 14, overflow: "hidden", ...(pad ? { padding: 16 } : {}) }}>{children}</section>;
}

function CardHeader({ icon, iconColor, title, badge, badgeTone = "neutral", action }: { icon: IconName; iconColor: string; title: string; badge?: string | undefined; badgeTone?: "accent" | "neutral"; action: { label: string; onClick: () => void } }) {
  return (
    <Row gap={8} align="center" style={{ padding: "14px 16px", borderBottom: "1px solid var(--border)" }}>
      <Icon name={icon} size={14} color={iconColor} />
      <span style={{ fontSize: 13, fontWeight: 700, color: "var(--text-1)" }}>{title}</span>
      {badge && <Chip tone={badgeTone === "accent" ? "accent" : "neutral"}>{badge}</Chip>}
      <div style={{ flex: 1 }} />
      <Btn variant="ghost" size="sm" iconRight={<Icon name="arrow-right" size={13} />} onClick={action.onClick}>{action.label}</Btn>
    </Row>
  );
}

function CardHeaderInline({ icon, iconColor, title, action }: { icon: IconName; iconColor: string; title: string; action: { label: string; onClick: () => void } }) {
  return (
    <Row gap={8} align="center">
      <Icon name={icon} size={14} color={iconColor} />
      <span style={{ fontSize: 13, fontWeight: 700, color: "var(--text-1)" }}>{title}</span>
      <div style={{ flex: 1 }} />
      <Btn variant="ghost" size="sm" iconRight={<Icon name="arrow-right" size={13} />} onClick={action.onClick}>{action.label}</Btn>
    </Row>
  );
}

function EmptyRow({ text }: { text: string }) {
  return <div style={{ padding: "16px", fontSize: 12.5, color: "var(--text-3)" }}>{text}</div>;
}

// ─── small helpers ────────────────────────────────────────────────────

function capitalize(s: string): string {
  return s ? s.charAt(0).toUpperCase() + s.slice(1) : s;
}

function greetingForHour(): string {
  const h = new Date().getHours();
  if (h < 12) return "Good morning";
  if (h < 18) return "Good afternoon";
  return "Good evening";
}

function todayLabel(): string {
  return new Date().toLocaleDateString(undefined, { weekday: "long", month: "short", day: "numeric" });
}

function fmtTime(iso: string | undefined): string {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit" });
}

/** "in 2h" / "in 14m" / "now" relative to a future scheduled time. */
function relUntil(iso: string | undefined): string {
  if (!iso) return "";
  const diff = Date.parse(iso) - Date.now();
  if (Number.isNaN(diff)) return "";
  if (diff <= 0) return "now";
  const min = Math.floor(diff / 60000);
  if (min < 60) return `in ${min}m`;
  const hr = Math.floor(min / 60);
  return `in ${hr}h`;
}
